import { OrganigrammeNode, NodeSide, OrganigrammePoint } from './node';
import { OrganigrammeEntityLink } from './entity-link';
import { filter, map } from 'lodash';

import {
  SVG_NS,
  NODE_FONT_FAMILY,
  LINK_FONT_SIZE,
  LINK_FONT_SIZE_PX,
  snapToGridX,
  snapToGridY,
  GRID_CELL_HEIGHT,
  GRID_CELL_WIDTH,
} from '../organigramme.settings';

type NameLabelPosition = 'MIDDLE' | 'FROM' | 'TO';

function formatShares(shares: number, suffix: string) {
  shares = Math.round(shares * 10000) / 100;

  return `${shares}% ${suffix}`;
}

export class OrganigrammeLink {

  private _id: string
  private _name: string
  private _fromNode: string
  private _toNode: string

  public fromPoint: OrganigrammePoint
  public toPoint: OrganigrammePoint
  public middlePoint1: OrganigrammePoint
  public middlePoint2: OrganigrammePoint

  public nameX: number
  public nameY: number

  public arrowX: number
  public arrowY: number
  public arrowR: number

  constructor(public from: OrganigrammeNode,
    public to: OrganigrammeNode,
    public entityLink: OrganigrammeEntityLink) {
    this._id = `${from.id}->${to.id}`;
    this._fromNode = from.id;
    this._toNode = to.id;

    // Build the name
    this._name = '';
    let total_shares = to.entity.total_shares;

    if (total_shares) {
      let values: string[] = [];

      if (entityLink.pp_shares) {
        values.push(formatShares(entityLink.pp_shares / total_shares, 'PP'));
      }

      if (entityLink.uf_shares) {
        values.push(formatShares(entityLink.uf_shares / total_shares, 'UF'));
      }

      if (entityLink.np_shares) {
        values.push(formatShares(entityLink.np_shares / total_shares, 'NP'));
      }

      if (entityLink.at_shares) {
        values.push(formatShares(entityLink.at_shares / total_shares, 'AT'));
      }

      this._name = values.join(', ');
    }
  }

  get id() {
    return this._id;
  }

  get name() {
    return this._name;
  }

  get points() {
    return filter([
      this.fromPoint,
      this.middlePoint1,
      this.middlePoint2,
      this.toPoint
    ], p => p !== null && p !== undefined);
  }

  get pointsString() {
    return map(this.points, p => `${p.x},${p.y}`).join(',');
  }

  getFromSide() {
    let toPoint = this.to.center;

    return this.from.getSide(toPoint);
  }

  getToSide() {
    let fromPoint = this.from.center;

    return this.to.getSide(fromPoint);
  }

  getCellsDistance() {
    // X distance
    const toLeftX = this.to.x;
    const toRightX = this.to.x + this.to.width;
    const fromLeftX = this.from.x;
    const fromRightX = this.from.x + this.from.width;

    const screenDistanceX = Math.min(Math.abs(toLeftX - fromLeftX), Math.abs(toRightX - fromRightX), Math.abs(toLeftX - fromRightX), Math.abs(toRightX - fromLeftX));


    // Y distance
    const toTopY = this.to.y;
    const toBottomY = this.to.y + this.to.height;
    const fromTopY = this.from.y;
    const fromBottomY = this.from.y + this.from.height;

    const screenDistanceY = Math.min(Math.abs(toTopY - fromTopY), Math.abs(toBottomY - fromBottomY), Math.abs(toTopY - fromBottomY), Math.abs(toBottomY - fromTopY));

    // Side from
    const toMiddleX = toLeftX + this.to.width/2;
    const fromMiddleX = fromLeftX + this.from.width/2;
    const toMiddleY = toTopY + this.to.height/2;
    const fromMiddleY = fromTopY + this.from.height/2;

    const toLeftX50 = toLeftX - this.to.width * .50;
    const toRightX50 = toRightX + this.to.width * .50;
    const toTopY50 = toTopY - this.to.height * .50;
    const toBottomY50 = toBottomY + this.to.height * .50;

    return {
      distanceX: Math.round(screenDistanceX / GRID_CELL_WIDTH),
      distanceY: Math.round(screenDistanceY / GRID_CELL_HEIGHT),
      sideX: toMiddleX > fromMiddleX ? NodeSide.LEFT : NodeSide.RIGHT,
      sideY: toMiddleY > fromMiddleY ? NodeSide.BOTTOM : NodeSide.TOP,
      sameAxisX: fromMiddleX > toLeftX50 && fromMiddleX < toRightX50,
      sameAxisY: fromMiddleY > toTopY50 && fromMiddleY < toBottomY50,
    }
  }

  layout() {
    const { distanceX, distanceY, sideX, sideY, sameAxisX, sameAxisY } = this.getCellsDistance();

    // console.log({ distanceX, distanceY, sideX, sideY, sameAxisX, sameAxisY });

    if (distanceX <= 1 && distanceY <= 1) {
      const oppositeSideX = sideX === NodeSide.LEFT ? NodeSide.RIGHT : NodeSide.LEFT;
      const oppositeSideY = sideY === NodeSide.TOP ? NodeSide.BOTTOM : NodeSide.TOP;
      
      if (sameAxisY) {
        this.toPoint = this.to.getAnchorForSide(sideX);
        this.fromPoint = new OrganigrammePoint(this.from.getAnchorForSide(oppositeSideX).x, this.toPoint.y);
        this.middlePoint1 = this.middlePoint2 = null;
        this.layoutArrow(sideX);
        this.layoutName(sideX === NodeSide.LEFT ? 'FROM' : 'TO');
      }
      else if (sameAxisX) {
        this.toPoint = this.to.getAnchorForSide(oppositeSideY);
        this.fromPoint = new OrganigrammePoint(this.toPoint.x, this.from.getAnchorForSide(sideY).y);
        this.middlePoint1 = this.middlePoint2 = null;
        this.layoutArrow(oppositeSideY);
        this.layoutName(sideY === NodeSide.TOP ? 'FROM' : 'TO');
      }
      else {
        this.fromPoint = this.from.getAnchorForSide(oppositeSideX);
        this.toPoint = this.to.getAnchorForSide(oppositeSideY);
        this.middlePoint1 = this.middlePoint2 = new OrganigrammePoint(this.toPoint.x, this.fromPoint.y);
        this.layoutArrow(oppositeSideY);
        this.layoutName();
      }
    }
    // Default layouting
    else {
      let fromSide = this.getFromSide();
      let toSide = this.getToSide();

      // Calculate points
      let fromPoint = this.from.getAnchorForSide(fromSide);
      let toPoint = this.to.getAnchorForSide(toSide);

      let middleX = Math.min(fromPoint.x, toPoint.x) + (Math.abs(fromPoint.x - toPoint.x) / 2);
      let middleY = Math.min(fromPoint.y, toPoint.y) + (Math.abs(fromPoint.y - toPoint.y) / 2);

      middleX = snapToGridX(middleX);
      middleY = snapToGridY(middleY);

      let middlePoint1, middlePoint2;

      // Add middle point 1
      if (fromSide === NodeSide.TOP || fromSide === NodeSide.BOTTOM) {
        middlePoint1 = new OrganigrammePoint(fromPoint.x, middleY);
      }
      else {
        middlePoint1 = new OrganigrammePoint(middleX, fromPoint.y);
      }

      // Add middle point 2
      if (toSide === NodeSide.TOP || toSide === NodeSide.BOTTOM) {
        middlePoint2 = new OrganigrammePoint(toPoint.x, middleY);
      }
      else {
        middlePoint2 = new OrganigrammePoint(middleX, toPoint.y);
      }

      // Fix issue when from/to sides are not facing each others
      if (middlePoint1.x !== middlePoint2.x && middlePoint1.y !== middlePoint2.y) {
        if (fromSide === NodeSide.TOP || fromSide === NodeSide.BOTTOM) {
          middlePoint2.x = middlePoint1.x;
          middlePoint1.y = middlePoint2.y;
        }
        else {
          middlePoint1.x = middlePoint2.x;
          middlePoint2.y = middlePoint1.y;
        }
      }

      this.fromPoint = fromPoint;
      this.toPoint = toPoint;
      this.middlePoint1 = middlePoint1;
      this.middlePoint2 = middlePoint2;

      this.layoutArrow();

      this.layoutName();
    }

  }

  layoutArrow(toSide: NodeSide = this.getToSide()) {
    // Calculate arrow data
    this.arrowX = this.toPoint.x;
    this.arrowY = this.toPoint.y;

    if (toSide === NodeSide.TOP) {
      this.arrowX += 5;
      this.arrowY += 2;
      this.arrowR = 180;
    }
    else if (toSide === NodeSide.BOTTOM) {
      this.arrowX -= 5;
      this.arrowY -= 2;
      this.arrowR = 0;
    }
    else if (toSide === NodeSide.LEFT) {
      this.arrowX += 2;
      this.arrowY -= 5;
      this.arrowR = 90;
    }
    else if (toSide === NodeSide.RIGHT) {
      this.arrowX -= 2;
      this.arrowY += 5;
      this.arrowR = 270;
    }
  }


  layoutName(position: NameLabelPosition = 'MIDDLE') {
    // Calculate name data
    if (position === 'MIDDLE') {
      this.nameX = Math.min(this.middlePoint1.x, this.middlePoint2.x) + (Math.abs(this.middlePoint1.x - this.middlePoint2.x) / 2);
      this.nameY = Math.min(this.middlePoint1.y, this.middlePoint2.y) + (Math.abs(this.middlePoint1.y - this.middlePoint2.y) / 2);
    }
    else if(position === 'FROM') {
      this.nameX = this.fromPoint.x;
      this.nameY = this.fromPoint.y;
    }
    else if(position === 'TO') {
      this.nameX = this.toPoint.x;
      this.nameY = this.toPoint.y;
    }

    // Snap to grid
    this.nameX = snapToGridX(this.nameX);
    this.nameY = snapToGridY(this.nameY);

    // Adjustments
    this.nameX += 4;
    this.nameY -= 4;
  }

  get segments() {

    if (this.fromPoint.x === this.toPoint.x || this.fromPoint.y === this.toPoint.y) {
      return [[this.fromPoint, this.toPoint]];
    }
    else {
      let segments: OrganigrammePoint[][] = [];
      let points = this.points;

      for (let i = 0; i < points.length - 1; i++) {
        let p1 = points[i];
        let p2 = points[i + 1];

        if (!p1.equals(p2)) {
          segments.push([p1, p2]);
        }
      }

      return segments;
    }
  }


  // Export
  exportData() {
    let data: any = {
      fromNode: this._fromNode,
      toNode: this._toNode,
    };

    if (this.name) {
      let nameBBox = this.nameBBox();

      data.name = {
        x: this.nameX,
        y: this.nameY - LINK_FONT_SIZE, // Adjust Y (relative to bottom of the text)
        width: nameBBox.width,
        height: nameBBox.height,
        text: this.name
      };
    }

    data.segments = map(this.segments, s => ({
      fromX: s[0].x,
      fromY: s[0].y,
      toX: s[1].x,
      toY: s[1].y,
    }));

    return data;
  }

  nameBBox() {
    // Calculate text width
    let $svg = document.createElementNS(SVG_NS, 'svg');
    $svg.setAttribute('class', 'organigramme');

    // Hide the svg element
    $svg.style.position = 'fixed';
    $svg.style.top = '-1000px';

    let $g = document.createElementNS(SVG_NS, 'g');
    $g.setAttribute('class', 'connector');

    let $text = document.createElementNS(SVG_NS, 'text');
    $text.style.fontFamily = NODE_FONT_FAMILY;
    $text.style.fontSize = LINK_FONT_SIZE_PX;
    $text.appendChild(document.createTextNode(this.name));

    $g.appendChild($text);
    $svg.appendChild($g);
    document.body.appendChild($svg);

    let textBBox = $text.getBBox();

    document.body.removeChild($svg);

    return textBBox;
  }
}